import { onUnmounted } from "vue";
import { onResync, onSocketEvent } from "@/socket";

/**
 * What the Operator's list of dataspaces listens to in the user's own room, and what each makes
 * stale — the Operator's counterpart of the Designer's `USER_EVENT_HANDLERS`, checked against
 * `UserEvent` in `server/src/socket/broadcast.rs` by `npm run lint:sync`.
 *
 * Both are about who reaches a dataspace without the dataspace's own room being involved: a
 * department's role decides what its members hold in the department's dataspaces
 * (`department:membership:changed`), and a space that moved in or out of a department, or whose
 * department access was edited, tells each user who gained or lost it (`space:access:changed`).
 * Either way the dataspace is, or is no longer, on the caller's list.
 */
export const DATASPACE_LIST_EVENT_HANDLERS: Record<string, readonly "dataspaceList"[]> = {
	"department:membership:changed": ["dataspaceList"],
	"space:access:changed": ["dataspaceList"],
};

/**
 * Whether `event` concerns the dataspace list: `space:access:changed` names the kind of space it is
 * about, and a project's access is the Designer's list's business, not this one's.
 */
export function concernsDataspaceList(event: string, payload: Record<string, unknown>): boolean {
	if (!(event in DATASPACE_LIST_EVENT_HANDLERS)) return false;
	return event !== "space:access:changed" || payload.kind !== "project";
}

/**
 * Calls `reload` when the user's room says the list of dataspaces is stale, and when the room had
 * to be read afresh (a reconnect, a missed number). For the list page, for as long as it is open:
 * the page keeps its own fetch and decides how to apply it — which of two overlapping reloads wins,
 * and that a reload does not flash the skeleton.
 */
export function useDataspaceListSync(reload: () => void) {
	const off: Array<() => void> = [];
	for (const event of Object.keys(DATASPACE_LIST_EVENT_HANDLERS)) {
		off.push(
			onSocketEvent(event, (payload) => {
				if (concernsDataspaceList(event, payload)) reload();
			}),
		);
	}
	off.push(
		onResync((room) => {
			if (room.startsWith("user:")) reload();
		}),
	);
	onUnmounted(() => {
		for (const stop of off) stop();
	});
}
